import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import red from 'sushiweb/tokens/color/red';
import Layout from 'sushiweb/Layout/Layout';
import Spinner from 'sushiweb/Spinner/Spinner';
import Search from 'sushiweb/Icons/all/Search';
import Download from 'sushiweb/Icons/all/Download';
import Breadcrumb from 'sushiweb/Breadcrumb/Breadcrumb';
import H5 from 'sushiweb/Typography/Heading/H5';

import {
  exportCSV,
  formatAmountInRupees,
  formatDateForInfoSections,
  formatDateinDDMMMYYY,
  formatEventsAmountValue,
  getCurrentDate,
  includesOption,
  indexOfOption,
  maskUptoLastNDigits,
  searchWithName,
} from '../../helpers';
import {
  REFRESH_LABEL,
  amountParkedEventDetailsPageInfo,
  amountToppedUpEventDetailsPageInfo,
  creditTransactionsStatusEnum,
  eventsAmountEnum,
  statusLabels,
  topupDateEventDetailsPageInfo,
} from '../common/components/constants';
import {
  BreadCrumbWrapper,
  DashboardWrapper,
  LayoutContainers,
  SearchContainerV2,
  SearchInput,
  SpinnerHolder,
  StatusFilterAndExportCSVContainer,
  TabHeadWrapper,
  TabWrapper,
  TableContainer,
} from '../common/styles';
import DetailsWrapper from '../common/components/DetailsWrapper/DetailsWrapper';
import ErrorPage from '../common/components/ErrorPage';
import { creditTransactionsHeadColumn } from '../common/components/DataTables/components/headColumns';
import DataTables from '../common/components/DataTables';
import { DASHBOARD_ROUTE, HOME_ROUTE, EVENT_DETAILS } from '../../constants/routes';
import StatusFilter from '../common/components/StatusFilter';

const EventDetails = ({
  eventDetails,
  programDetails,
  creditTransactions,
  getEventDetails,
  fetchingEventDetails,
  fetchedEventDetails,
}) => {
  const keyFields = ['email_id'];
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const { programId, eventId } = useParams();
  const [search, onInputChange] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [2, 6, 8],
      label: 'INITIATED_PROCESSING',
      disabled: true,
    },
    {
      value: [3],
      label: 'WALLET_CREATION_PENDING_ONBOARDING_PENDING',
      disabled: true,
    },
    {
      value: [4],
      label: 'SUCCESS',
      disabled: true,
    },
    {
      value: [5, 7],
      label: 'FAILED',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  useEffect(() => {
    getEventDetails({
      programId,
      eventId,
    });
  }, []);

  useEffect(() => {
    if (!fetchingEventDetails && fetchedEventDetails) {
      if (creditTransactions) {
        creditTransactions.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        if (creditTransactions.length === 0) {
          setDisabledSearch(true);
        }
        setTableData(creditTransactions);
        setCurrentData(creditTransactions.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingEventDetails]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingEventDetails) {
        const arr = searchWithName(creditTransactions, search, keyFields);
        const availableStatuses = [];
        arr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = arr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(arr);
        }
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingEventDetails]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'email_id', value: 'Email ID' },
      { key: 'created_on', value: 'Actual top-up date' },
      { key: 'actual_amt', value: 'Amount topped-up' },
      { key: 'status', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = {};
      labelledData.email_id = data.email_id;
      labelledData.actual_amt = formatAmountInRupees(String(data.actual_amt));
      labelledData.created_on = formatDateinDDMMMYYY(data.created_on);
      labelledData.status =
        statusLabels[creditTransactionsStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `${programDetails.name} - Topups - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadCSVStyle = {
    paddingLeft: '5px',
    paddingRight: '20px',
    cursor: 'pointer',
  };

  const refreshPage = () => {
    getEventDetails({
      programId,
      eventId,
    });
  };

  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };
  const clearFilters = () => {
    setCurrentFilters([]);
  };

  return fetchingEventDetails ? (
    <DashboardWrapper>
      <SpinnerHolder className='center'>
        <Spinner color={red.z_red} size='medium' />
      </SpinnerHolder>
    </DashboardWrapper>
  ) : (
    <DashboardWrapper>
      <BreadCrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${HOME_ROUTE}`}
          >
            Home
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}`}
          >
            Past Top-ups
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
            linkTo={`${DASHBOARD_ROUTE}#${EVENT_DETAILS}`}
          >
            {maskUptoLastNDigits(eventId, 6)}
          </Breadcrumb.Link>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <TabHeadWrapper>
        <H5>{programDetails?.name ?? 'Program event Name'}</H5>
      </TabHeadWrapper>
      <LayoutContainers>
        <Layout gap={20}>
          <Layout.Item colD={4} colT={4} colM={4}>
            <DetailsWrapper
              showInfoTip
              tooltipDirection='bottomRight'
              tooltipMessage={topupDateEventDetailsPageInfo}
              label='Planned date'
              value={formatDateForInfoSections(eventDetails?.created_on)}
            />
          </Layout.Item>
          <Layout.Item colD={4} colT={4} colM={4}>
            <DetailsWrapper
              showInfoTip
              tooltipDirection='bottomRight'
              tooltipMessage={amountToppedUpEventDetailsPageInfo}
              label='Amount topped-up'
              value={formatAmountInRupees(
                eventDetails?.disbursed_amount?.value,
              )}
            />
          </Layout.Item>
          <Layout.Item colD={4} colT={4} colM={4}>
            <DetailsWrapper
              showInfoTip
              tooltipDirection='bottomRight'
              tooltipMessage={amountParkedEventDetailsPageInfo}
              label='Amount pending'
              value={formatEventsAmountValue(
                eventDetails,
                eventsAmountEnum.PENDING,
                false,
              )}
            />
          </Layout.Item>
        </Layout>
      </LayoutContainers>
      <TabHeadWrapper>
        <H5>Employees top-up details</H5>
      </TabHeadWrapper>
      <TabWrapper>
        {!fetchedEventDetails ? (
          <ErrorPage
            showAction
            actionLabel={REFRESH_LABEL}
            clickAction={refreshPage}
          />
        ) : (
          <>
            <SearchContainerV2>
              <SearchInput
                leftIcon={<Search />}
                innerWidth='84%'
                value={search}
                onChange={onInputChange}
                onFocus={() => setIsSearchClicked(true)}
                onBlur={() => setIsSearchClicked(false)}
                label={
                  isSearchClicked || disabledSearch || search?.length ? (
                    'Search by employee email id'
                  ) : (
                    <span style={{ fontSize: '16px', fontWeight: 300 }}>
                      {' '}
                      Search by employee email id
                      {' '}
                    </span>
                  )
                }
                disabled={disabledSearch}
              />
              <StatusFilterAndExportCSVContainer>
                <StatusFilter
                  clearFilters={clearFilters}
                  currentFilters={currentFilters}
                  options={filterStatusOptions}
                  handleSelect={handleSelect}
                />
                <Download
                  color='grey'
                  onClick={exportSearchResultCSV}
                  style={downloadCSVStyle}
                />
              </StatusFilterAndExportCSVContainer>
            </SearchContainerV2>
            <TableContainer>
              <DataTables
                columns={creditTransactionsHeadColumn}
                loading={fetchingEventDetails || loading}
                data={currentData}
                pagination
                totalRows={searchResults ? searchResults.length : 0}
                handlePageChange={handlePageChange}
                onSort={handleSort}
                emptyStateTitle='No employee top-up details'
              />
            </TableContainer>
          </>
        )}
      </TabWrapper>
    </DashboardWrapper>
  );
};

EventDetails.propTypes = {
  eventDetails: PropTypes.objectOf(PropTypes.any),
  programDetails: PropTypes.objectOf(PropTypes.any),
  creditTransactions: PropTypes.objectOf(PropTypes.any),
  getEventDetails: PropTypes.func,
  fetchingEventDetails: PropTypes.bool,
  fetchedEventDetails: PropTypes.bool,
};

EventDetails.defaultProps = {
  eventDetails: {},
  programDetails: {},
  creditTransactions: {},
  getEventDetails: _noop,
  fetchingEventDetails: true,
  fetchedEventDetails: true,
};

export default EventDetails;
